import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Calendar, User, Flag, AlignLeft, CheckSquare, X, Users, Tag, Paperclip } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';

const PRIORITY_CONFIG = {
  'baixa': { label: 'Baixa', color: 'text-slate-500', bg: 'bg-slate-100', icon: 'text-slate-400' },
  'media': { label: 'Média', color: 'text-blue-600', bg: 'bg-blue-100', icon: 'text-blue-500' },
  'alta': { label: 'Alta', color: 'text-orange-600', bg: 'bg-orange-100', icon: 'text-orange-500' },
  'urgente': { label: 'Urgente', color: 'text-red-600', bg: 'bg-red-100', icon: 'text-red-500' }
};

export default function ProjectTasksModal({ project, isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Create mode
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('media');
  const [newTaskAssignee, setNewTaskAssignee] = useState(user?.id || 'none');
  const [newTaskDate, setNewTaskDate] = useState('');
  
  // Description Edit
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [description, setDescription] = useState('');
  
  // Sidebar State
  const [projectLabels, setProjectLabels] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [projectDates, setProjectDates] = useState({ inicio: '', fim: '' });
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (project) {
      setTasks(project.tasks || []);
      setDescription(project.description || '');
      setProjectLabels(project.etiquetas || []);
      setProjectMembers(project.membros || []);
      setProjectDates({ 
        inicio: project.data_inicio ? new Date(project.data_inicio).toISOString().split('T')[0] : '',
        fim: project.prazo_final ? new Date(project.prazo_final).toISOString().split('T')[0] : ''
      });
      setIsAddingTask(false);
      setNewTaskTitle('');
    }
  }, [project, isOpen]);

  useEffect(() => {
    if (isOpen) fetchUsers();
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Erro ao buscar usuários', error);
    }
  };

  const handleSaveProjectPayload = async (payloadOverride) => {
    try {
      const payload = {
        ...project,
        ...payloadOverride
      };
      
      if(payloadOverride.tasks) {
        payload.tasks = payloadOverride.tasks.map(t => ({
          ...t,
          assignee_id: t.assignee_id === 'none' ? null : t.assignee_id
        }));
      }

      await api.put(`/projects/${project.id}`, payload);
      if(payloadOverride.tasks) setTasks(payloadOverride.tasks);
      if(payloadOverride.description !== undefined) setDescription(payloadOverride.description);
      
      if(onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erro ao atualizar projeto');
    }
  };

  const saveDescription = () => {
    if (description !== project.description) {
      handleSaveProjectPayload({ description });
    }
    setIsEditingDescription(false);
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;

    const updatedTasks = [...tasks, {
      id: crypto.randomUUID(),
      title: newTaskTitle,
      status: 'pendente',
      priority: newTaskPriority,
      assignee_id: newTaskAssignee !== 'none' ? newTaskAssignee : null,
      due_date: newTaskDate ? new Date(newTaskDate).toISOString().split('T')[0] : null
    }];

    handleSaveProjectPayload({ tasks: updatedTasks });
    setNewTaskTitle('');
    setNewTaskDate('');
    setNewTaskPriority('media');
    // keep assignee the same for rapid multi-adding for the same person
    // setIsAddingTask(false); // don't close, feel like Trello multiple entry
  };

  const handleToggleTask = (taskId, checked) => {
    const updatedTasks = tasks.map(t => 
      t.id === taskId ? { ...t, status: checked ? 'concluida' : 'pendente' } : t
    );
    handleSaveProjectPayload({ tasks: updatedTasks });
  };

  const removeTask = (taskId) => {
    const updatedTasks = tasks.filter(t => t.id !== taskId);
    handleSaveProjectPayload({ tasks: updatedTasks });
  };
  
  // Sidebar Logic
  const toggleLabel = (label) => {
    const exists = projectLabels.find(l => l.name === label.name);
    let updated;
    if (exists) {
      updated = projectLabels.filter(l => l.name !== label.name);
    } else {
      updated = [...projectLabels, label];
    }
    setProjectLabels(updated);
    handleSaveProjectPayload({ etiquetas: updated });
  };

  const toggleMember = (memberId) => {
    if (memberId === 'none') return;
    let updated;
    if (projectMembers.includes(memberId)) {
      updated = projectMembers.filter(id => id !== memberId);
    } else {
      updated = [...projectMembers, memberId];
    }
    setProjectMembers(updated);
    handleSaveProjectPayload({ membros: updated });
  };

  const updateDates = (type, val) => {
    const updated = { ...projectDates, [type]: val };
    setProjectDates(updated);
    handleSaveProjectPayload({ 
      data_inicio: updated.inicio ? new Date(updated.inicio).toISOString() : project.data_inicio,
      prazo_final: updated.fim ? new Date(updated.fim).toISOString() : project.prazo_final
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Simulação de upload (em produção enviaria para S3/etc)
    const newAnexos = [...(project.anexos || []), {
      id: crypto.randomUUID(),
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file), // temporário
      created_at: new Date().toISOString()
    }];
    handleSaveProjectPayload({ anexos: newAnexos });
    toast.success('Arquivo anexado com sucesso');
  };

  const handleArchiveProject = async () => {
    try {
      await api.put(`/projects/${project.id}`, { ...project, status: 'arquivado' });
      toast.success('Projeto arquivado');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erro ao arquivar projeto');
    }
  };

  // Checkbox calculations
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'concluida').length;
  const progressPercent = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] bg-[#f4f5f7] flex flex-col p-0 overflow-hidden text-slate-800 border-0 rounded-xl [&>button:last-child]:hidden">
        
        {/* Trello Header Style */}
        <div className="pt-6 px-6 pb-2">
          <div className="flex items-start gap-4">
             <div className="mt-1"><Flag className="h-5 w-5 text-slate-600" /></div>
             <div className="flex-1">
               <h2 className="text-xl font-semibold text-slate-900 leading-tight mb-1">{project.name}</h2>
               <p className="text-sm text-slate-500">na coluna <span className="underline cursor-pointer">{project.status}</span></p>
               
               {/* Labels & Members Summary */}
               <div className="flex flex-wrap gap-4 mt-4">
                  {projectLabels.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Etiquetas</h4>
                      <div className="flex flex-wrap gap-1">
                        {projectLabels.map(l => (
                          <div key={l.name} className={`h-8 min-w-[40px] px-3 rounded-md flex items-center justify-center text-xs font-bold text-white shadow-sm ${l.color}`}>
                            {l.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {projectMembers.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Membros</h4>
                      <div className="flex flex-wrap gap-1">
                        {projectMembers.map(id => {
                          const u = users.find(user => user.id === id);
                          return (
                            <div key={id} className="w-8 h-8 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-slate-700 border-2 border-white shadow-sm" title={u?.name}>
                              {u?.name?.charAt(0).toUpperCase() || '?'}
                            </div>
                          );
                        })}
                        <button className="w-8 h-8 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-500 transition-colors">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
               </div>
             </div>
             <button
               onClick={onClose}
               className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors flex-shrink-0"
             >
               <X className="h-4 w-4" />
             </button>
          </div>
        </div>

        {/* 2 Column Layout */}
        <div className="flex-1 overflow-y-auto px-6 pb-8 flex flex-col md:flex-row gap-8">
          
          {/* LEFT COLUMN: Main Content */}
          <div className="flex-1 flex flex-col gap-8 min-w-0 pr-2">
            
            {/* Description Section */}
            <section className="flex gap-4">
              <div><AlignLeft className="h-5 w-5 text-slate-600 mt-1" /></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Descrição</h3>
                  {!isEditingDescription && description && (
                     <Button variant="secondary" size="sm" className="h-8 text-xs bg-slate-200/60 hover:bg-slate-300/60 text-slate-700" onClick={() => setIsEditingDescription(true)}>Editar</Button>
                  )}
                </div>
                {isEditingDescription ? (
                  <div className="flex flex-col gap-2">
                    <textarea 
                      className="w-full rounded-md border-0 bg-white p-3 text-sm shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 min-h-[100px] resize-y outline-none"
                      placeholder="Adicione uma descrição mais detalhada..."
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                       <Button onClick={saveDescription} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">Salvar</Button>
                       <Button variant="ghost" size="sm" onClick={() => setIsEditingDescription(false)}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => setIsEditingDescription(true)}
                    className={`w-full rounded-md p-3 text-sm cursor-pointer transition-colors ${description ? 'text-slate-700 whitespace-pre-wrap' : 'bg-slate-200/50 hover:bg-slate-300/50 text-slate-500 font-medium'}`}
                  >
                     {description || 'Adicione uma descrição mais detalhada...'}
                  </div>
                )}
              </div>
            </section>

            {/* Checklist Section */}
            <section className="flex gap-4">
              <div><CheckSquare className="h-5 w-5 text-slate-600 mt-0.5" /></div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Tarefas</h3>
                  {totalTasks > 0 && (
                     <Button variant="secondary" size="sm" className="h-8 text-xs bg-slate-200/60 hover:bg-slate-300/60 text-slate-700">Ocultar concluídas</Button>
                  )}
                </div>

                {/* Progress Bar */}
                {totalTasks > 0 && (
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-semibold text-slate-500 w-8">{progressPercent}%</span>
                    <Progress value={progressPercent} className={`h-2 flex-1 ${progressPercent === 100 ? 'bg-green-100 [&>div]:bg-green-500' : 'bg-slate-200'}`} />
                  </div>
                )}

                {/* Tasks List */}
                <div className="flex flex-col gap-1 mb-4">
                  {tasks.map(task => {
                    const isChecked = task.status === 'concluida';
                    return (
                      <div key={task.id} className="group relative flex items-start gap-3 p-2 rounded hover:bg-slate-200/40 transition-colors">
                        <Checkbox 
                          checked={isChecked}
                          onCheckedChange={(c) => handleToggleTask(task.id, c)}
                          className="mt-1 rounded border-slate-300 w-4 h-4 shadow-sm checked:bg-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        />
                        <div className={`flex-1 text-sm pt-0.5 leading-snug flex flex-wrap items-center gap-2 ${isChecked ? 'line-through text-slate-500' : 'text-slate-800'}`}>
                           <span>{task.title}</span>
                           
                           {/* Badges para propriedades vizuais */}
                           {(task.assignee_id || task.due_date || task.priority !== 'media') && (
                             <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                               {task.priority !== 'media' && (
                                 <span className={`inline-flex items-center text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700`}>
                                   {task.priority}
                                 </span>
                               )}
                               {task.due_date && (
                                 <span className="inline-flex flex-shrink-0 items-center justify-center gap-1 text-[11px] font-medium bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-sm">
                                   <Calendar className="h-3 w-3" />
                                   {new Date(task.due_date).toLocaleDateString('pt-BR', {day:'2-digit', month:'short'})}
                                 </span>
                               )}
                               {task.assignee_id && (
                                 <span className="inline-flex flex-shrink-0 items-center justify-center w-5 h-5 rounded-full bg-slate-300 text-[10px] font-bold text-slate-700" title={users.find(u => u.id === task.assignee_id)?.name}>
                                   {users.find(u => u.id === task.assignee_id)?.name.charAt(0).toUpperCase() || 'U'}
                                 </span>
                               )}
                             </div>
                           )}
                        </div>

                        {/* Actions (Delete only for simplicity) */}
                        <div className="opacity-0 group-hover:opacity-100 absolute right-2 top-2 bg-slate-100 rounded-md">
                          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500 hover:text-red-600" onClick={() => removeTask(task.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Add Task Box similar to Trello */}
                {isAddingTask ? (
                  <div className="bg-white rounded border border-slate-300 shadow-sm p-3 mt-2 flex flex-col gap-3">
                     <Input 
                        placeholder="Adicione um item" 
                        value={newTaskTitle}
                        onChange={e => setNewTaskTitle(e.target.value)}
                        className="border-0 bg-transparent px-0 focus-visible:ring-0 shadow-none text-sm font-medium"
                        autoFocus
                        onKeyDown={e => { if(e.key === 'Enter') handleAddTask() }}
                     />
                     
                     <div className="flex flex-wrap items-center gap-2 text-xs border-t border-slate-100 pt-2 border-dashed">
                        {/* Fake labels over select components for native speed */}
                        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-600 cursor-pointer relative">
                          <User className="h-3.5 w-3.5" /> Membro
                          <select className="absolute inset-0 w-full opacity-0 cursor-pointer" value={newTaskAssignee} onChange={e => setNewTaskAssignee(e.target.value)}>
                            <option value="none">Nenhum</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                          {newTaskAssignee !== 'none' && <span className="ml-1 text-slate-800 bg-slate-300 px-1 rounded-sm">{users.find(u=>u.id===newTaskAssignee)?.name.split(' ')[0]}</span>}
                        </div>

                        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-600 cursor-pointer relative">
                          <Tag className="h-3.5 w-3.5" /> Prioridade
                          <select className="absolute inset-0 w-full opacity-0 cursor-pointer" value={newTaskPriority} onChange={e => setNewTaskPriority(e.target.value)}>
                            <option value="media">Média</option>
                            <option value="baixa">Baixa</option>
                            <option value="alta">Alta</option>
                            <option value="urgente">Urgente</option>
                          </select>
                        {newTaskPriority !== 'media' && <span className={`ml-1 px-1 rounded-sm ${PRIORITY_CONFIG[newTaskPriority]?.color}`}>{PRIORITY_CONFIG[newTaskPriority]?.label}</span>}
                        </div>

                        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-600 cursor-pointer relative overflow-hidden">
                          <Calendar className="h-3.5 w-3.5" /> 
                          {newTaskDate ? new Date(newTaskDate).toLocaleDateString('pt-BR') : 'Data'}
                          <input type="date" className="absolute top-0 right-0 h-full w-[200%] opacity-0 cursor-pointer" value={newTaskDate} onChange={e => setNewTaskDate(e.target.value)} />
                        </div>
                     </div>

                     <div className="flex items-center gap-2 mt-1">
                        <Button onClick={handleAddTask} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">Adicionar</Button>
                        <Button onClick={() => setIsAddingTask(false)} size="sm" variant="ghost" className="text-slate-600">Cancelar</Button>
                     </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingTask(true)}
                    className="mt-2 flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors border-2 border-dashed border-slate-300 hover:border-slate-400"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar Tarefa
                  </button>
                )}

              </div>
            </section>

          </div>

          {/* RIGHT COLUMN: Sidebar Actions */}
          <div className="w-full md:w-[180px] flex-shrink-0 flex flex-col gap-6">
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 px-1">Adicionar ao cartão</h4>
              <div className="flex flex-col gap-1.5">
                
                {/* Membros Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full">
                      <User className="h-4 w-4 mr-2 text-slate-600" /> Membros
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3 bg-white border border-slate-200 shadow-xl rounded-lg">
                    <h5 className="text-xs font-bold text-slate-500 uppercase mb-3 text-center">Membros da Equipe</h5>
                    <div className="flex flex-col gap-1">
                      {users.map(u => (
                        <div 
                          key={u.id} 
                          className="flex items-center gap-2 p-2 rounded hover:bg-slate-100 cursor-pointer text-sm"
                          onClick={() => toggleMember(u.id)}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${projectMembers.includes(u.id) ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="flex-1 truncate">{u.name}</span>
                          {projectMembers.includes(u.id) && <CheckSquare className="h-3.5 w-3.5 text-blue-600" />}
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Etiquetas Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full">
                      <Tag className="h-4 w-4 mr-2 text-slate-600" /> Etiquetas
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3 bg-white border border-slate-200 shadow-xl rounded-lg">
                    <h5 className="text-xs font-bold text-slate-500 uppercase mb-3 text-center">Etiquetas</h5>
                    <div className="flex flex-col gap-2">
                       {[
                         { name: 'Urgente', color: 'bg-red-500' },
                         { name: 'Prioritário', color: 'bg-orange-500' },
                         { name: 'Em pausa', color: 'bg-yellow-500' },
                         { name: 'Concluído', color: 'bg-green-500' },
                         { name: 'Aguardando', color: 'bg-blue-500' },
                         { name: 'Revisão', color: 'bg-purple-500' }
                       ].map(label => (
                         <div 
                            key={label.name}
                            onClick={() => toggleLabel(label)}
                            className={`h-8 rounded cursor-pointer transition-transform hover:scale-[1.02] flex items-center px-3 text-white text-xs font-bold shadow-sm ${label.color} relative animate-in fade-in zoom-in duration-200`}
                         >
                            {label.name}
                            {projectLabels.find(l => l.name === label.name) && (
                              <CheckSquare className="h-4 w-4 ml-auto text-white/80" />
                            )}
                         </div>
                       ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Checklist Button (Shortcut to Add Task) */}
                <Button 
                  variant="secondary" 
                  onClick={() => { setIsAddingTask(true); }}
                  className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full"
                >
                  <CheckSquare className="h-4 w-4 mr-2 text-slate-600" /> Checklist
                </Button>

                {/* Datas Popover */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full">
                      <Calendar className="h-4 w-4 mr-2 text-slate-600" /> Datas
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-4 bg-white border border-slate-200 shadow-xl rounded-lg flex flex-col gap-4">
                    <h5 className="text-xs font-bold text-slate-500 uppercase text-center">Datas do Projeto</h5>
                    <div className="flex flex-col gap-3">
                       <div className="flex flex-col gap-1">
                          <Label className="text-[10px] text-slate-500 uppercase font-bold">Data Início</Label>
                          <Input type="date" value={projectDates.inicio} onChange={e => updateDates('inicio', e.target.value)} />
                       </div>
                       <div className="flex flex-col gap-1">
                          <Label className="text-[10px] text-slate-500 uppercase font-bold">Prazo Final</Label>
                          <Input type="date" value={projectDates.fim} onChange={e => updateDates('fim', e.target.value)} />
                       </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Anexo Button with hidden input */}
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                <Button 
                   variant="secondary" 
                   onClick={() => fileInputRef.current?.click()}
                   className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full"
                >
                  <Paperclip className="h-4 w-4 mr-2 text-slate-600" /> Anexo
                </Button>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2 px-1">Ações</h4>
              <div className="flex flex-col gap-1.5">
                <Button 
                  variant="secondary" 
                  onClick={() => setIsDeleteAlertOpen(true)}
                  className="justify-start h-8 px-3 bg-slate-200/60 hover:bg-slate-300/80 text-slate-700 text-sm font-medium w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Excluir
                </Button>
              </div>
            </div>
          </div>

          {/* Delete/Archive Confirmation */}
          <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
            <AlertDialogContent className="bg-white rounded-xl border-0 shadow-2xl scale-in-center">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-xl font-bold text-slate-900">Arquivar Projeto?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-500">
                  Isso moverá o projeto para o arquivo. Ele não será mais exibido no Kanban principal, mas poderá ser recuperado posteriormente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="mt-4 gap-2">
                <AlertDialogCancel className="bg-slate-100 hover:bg-slate-200 border-0 text-slate-700 font-semibold rounded-lg px-6">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchiveProject} className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg px-6 shadow-md shadow-red-200 transition-all active:scale-95">
                  Confirmar Arquivamento
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>
      </DialogContent>
    </Dialog>
  );
}
